// Bank Accounts management — the master list of bank accounts. Data comes from
// the cached useBankAccounts() hook; create/update/delete invalidate the
// ["bank-accounts"] query to refresh. Add/Edit happen in a modal; the eye icon
// opens a read-only detail drawer.
import { useState, useContext } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import DashboardLayout from "../layouts/DashboardLayout";
import { useThemeColors } from "../context/ThemeContext";
import GlassCard from "../components/GlassCard";
import { glassRowSx } from "../components/glassRow";
import { HEADING_FONT } from "../theme/fonts";
import BankAccountModal from "../components/BankAccountModal";
import BankAccountDetailDrawer from "../components/BankAccountDetailDrawer";
import {
  useBankAccounts,
  useDeleteBankAccount,
  useBulkDeleteBankAccounts,
} from "../hooks/queries";
import { useToast } from "../components/ToastProvider";
import { useConfirm } from "../components/ConfirmProvider";
import { useTableSelection } from "../hooks/useTableSelection";
import SelectionActionBar from "../components/SelectionActionBar";
import { AuthContext } from "../context/AuthContext";

const BANK_GRADIENT = "linear-gradient(135deg, #34d399 0%, #10b981 100%)";

// Format a MySQL datetime string ("2025-06-02 16:30:59") to "02-06-2025 16:30".
function fmtDateTime(v) {
  if (!v) return "—";
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]} ${m[4]}:${m[5]}`;
  const d = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return d ? `${d[3]}-${d[2]}-${d[1]}` : String(v);
}

function BankAccounts() {
  const colors = useThemeColors();
  const toast = useToast();
  const confirm = useConfirm();

  const { hasPermission } = useContext(AuthContext);
  const canAdd = hasPermission("bankAccounts", "add");
  const canEdit = hasPermission("bankAccounts", "edit");
  const canDelete = hasPermission("bankAccounts", "delete");

  const { data: accountsData } = useBankAccounts();
  const accounts = Array.isArray(accountsData) ? accountsData : accountsData?.rows || [];

  const deleteAccount = useDeleteBankAccount();
  const bulkDelete = useBulkDeleteBankAccounts();

  // --- Add / Edit modal ---
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  // --- View drawer ---
  const [viewAccount, setViewAccount] = useState(null);

  function openAdd() {
    setEditId(null);
    setModalOpen(true);
  }

  function openEdit(account) {
    setViewAccount(null);
    setEditId(account.id);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditId(null);
  }

  function handleModalSuccess() {
    closeModal();
  }

  // --- Delete ---
  async function handleDelete(id) {
    try {
      await deleteAccount.mutateAsync(id);
      toast.success("Bank account deleted");
    } catch (err) {
      toast.error(err.message || "Failed to delete bank account");
    }
  }

  // --- Row selection + bulk delete ---
  const pageIds = accounts.map((a) => a.id);
  const {
    selectedIds,
    toggleRow,
    toggleAll,
    clear: clearSelection,
    allSelected,
    someSelected,
  } = useTableSelection(pageIds);
  const [bulkBusy, setBulkBusy] = useState(false);

  async function handleDeleteSelected() {
    const ids = [...selectedIds];
    if (!ids.length) return;
    const ok = await confirm({
      title: `Delete ${ids.length} bank account${ids.length === 1 ? "" : "s"}?`,
      message: "The selected bank accounts will be permanently removed.",
      danger: true,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const res = await bulkDelete.mutateAsync(ids);
      clearSelection();
      toast.success(`Deleted ${res?.deleted ?? ids.length} bank account(s)`);
    } catch (err) {
      toast.error(err.message || "Failed to delete selected bank accounts");
    } finally {
      setBulkBusy(false);
    }
  }

  // checkbox | # | Account Name | Country | Bank | Account No | IBAN | IFSC |
  // Branch | Location | GST | Gpay/Paytm/Phonepay | Posted By | Posted On |
  // Updated On | Actions
  //
  // Every data column gets a comfortable fixed width so nothing squeezes; the
  // table scrolls horizontally inside its card when it can't fit. Column gap is
  // uniform so spacing between columns is even across the whole row.
  const GRID_COLUMNS =
    "48px 52px 230px 150px 170px 170px 190px 150px 180px 180px 90px 190px 140px 160px 160px 130px";
  const COL_GAP = 3;

  return (
    <DashboardLayout>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 4, justifyContent: "space-between" }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: HEADING_FONT,
              fontWeight: 700,
              color: colors.text,
              letterSpacing: "-0.5px",
            }}
          >
            Bank Accounts
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textMuted, mt: 0.5 }}>
            {accounts.length} {accounts.length === 1 ? "account" : "accounts"}
          </Typography>
        </Box>

        {canAdd && (
          <Button
            onClick={openAdd}
            startIcon={<AddIcon />}
            sx={{
              alignSelf: { xs: "flex-end", sm: "center" },
              ml: { sm: "auto" },
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              px: 2.5,
              py: 1.1,
              borderRadius: 2,
              color: "#fff",
              background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
              boxShadow: "0 4px 14px rgba(79, 70, 229, 0.35)",
              "&:hover": {
                boxShadow: "0 8px 22px rgba(79, 70, 229, 0.45)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.15s ease",
            }}
          >
            Add Bank Account
          </Button>
        )}
      </Stack>

      {/* Bulk-delete selection bar */}
      <SelectionActionBar
        count={selectedIds.size}
        onDelete={canDelete ? handleDeleteSelected : undefined}
        onClear={clearSelection}
        busy={bulkBusy}
        colors={colors}
      />

      {/* Table */}
      <GlassCard sx={{ overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          {/* Table header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: GRID_COLUMNS,
              minWidth: "max-content",
              gap: COL_GAP,
              px: 3,
              py: 1.75,
              alignItems: "center",
              borderBottom: `1px solid ${colors.divider}`,
              background: colors.hover,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={toggleAll}
                sx={{ p: 0, color: colors.textMuted }}
              />
            </Box>
            {["#", "Account Name", "Country", "Bank", "Account No", "IBAN Number", "IFSC", "Branch", "Location", "GST", "UPI (UPI ID / Phone No)", "Posted By", "Posted On", "Updated On", "Actions"].map((h) => (
              <Typography
                key={h}
                sx={{
                  fontFamily: HEADING_FONT,
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: colors.textMuted,
                  textAlign: "center",
                  ml: h === "Account Name" ? "-24px" : 0,
                }}
              >
                {h}
              </Typography>
            ))}
          </Box>

          {/* Rows */}
          {accounts.length === 0 ? (
            <Box sx={{ py: 8, px: 3, textAlign: "center" }}>
              <AccountBalanceOutlinedIcon
                sx={{ fontSize: 44, color: colors.textFaint, mb: 1 }}
              />
              <Typography
                sx={{ fontFamily: HEADING_FONT, fontSize: 15, fontWeight: 600, color: colors.text }}
              >
                No bank accounts yet
              </Typography>
              <Typography sx={{ fontSize: 13, color: colors.textMuted, mt: 0.5 }}>
                Click "Add Bank Account" to create your first one
              </Typography>
            </Box>
          ) : (
            accounts.map((acc, idx) => (
              <Box
                key={acc.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: GRID_COLUMNS,
                  minWidth: "max-content",
                  gap: COL_GAP,
                  px: 3,
                  py: 1.75,
                  alignItems: "center",
                  borderBottom:
                    idx < accounts.length - 1
                      ? `1px solid ${colors.divider}`
                      : "none",
                  ...glassRowSx({ idx, accent: BANK_GRADIENT, hoverBg: colors.hover }),
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(acc.id)}
                    onChange={() => toggleRow(acc.id)}
                    sx={{ p: 0, color: colors.textMuted }}
                  />
                </Box>
                <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted, textAlign: "center" }}>
                  {idx + 1}
                </Typography>
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                      background: BANK_GRADIENT,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow: "0 4px 12px rgba(16, 185, 129, 0.35)",
                    }}
                  >
                    <AccountBalanceOutlinedIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Box sx={{ minWidth: 0, height: 34, display: "flex", alignItems: "center" }}>
                    <Typography
                      noWrap
                      sx={{ fontFamily: HEADING_FONT, fontSize: 14, fontWeight: 600, color: colors.text, lineHeight: 1 }}
                    >
                      {acc.accountName || "—"}
                    </Typography>
                  </Box>
                </Stack>
                <Typography noWrap sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted, textAlign: "center" }}>
                  {acc.country || "—"}
                </Typography>
                <Typography noWrap sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted, textAlign: "center" }}>
                  {acc.bank || "—"}
                </Typography>
                <Typography noWrap sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted, textAlign: "center" }}>
                  {acc.accountNumber || "—"}
                </Typography>
                <Typography noWrap sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted, textAlign: "center" }}>
                  {acc.ibanNumber || "—"}
                </Typography>
                <Typography noWrap sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted, textAlign: "center" }}>
                  {acc.ifscCode || "—"}
                </Typography>
                <Typography noWrap sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted, textAlign: "center" }}>
                  {acc.branch || "—"}
                </Typography>
                <Typography noWrap sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted, textAlign: "center" }}>
                  {acc.location || "—"}
                </Typography>
                <Typography noWrap sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted, textAlign: "center" }}>
                  {acc.gst || "—"}
                </Typography>
                <Typography noWrap sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted, textAlign: "center" }}>
                  {acc.gpayPaytmPhonepe || "—"}
                </Typography>
                <Typography noWrap sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted, textAlign: "center" }}>
                  {acc.postedBy || "—"}
                </Typography>
                <Typography noWrap sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted, textAlign: "center" }}>
                  {fmtDateTime(acc.createdAt)}
                </Typography>
                <Typography noWrap sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted, textAlign: "center" }}>
                  {fmtDateTime(acc.updatedAt)}
                </Typography>
                <Stack direction="row" spacing={0.5} justifyContent="center">
                  <Tooltip title="View">
                    <IconButton
                      size="small"
                      onClick={() => setViewAccount(acc)}
                      sx={{
                        color: colors.textMuted,
                        "&:hover": { background: colors.hover, color: "#10b981" },
                      }}
                    >
                      <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  {canEdit && (
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => openEdit(acc)}
                        sx={{
                          color: colors.textMuted,
                          "&:hover": { background: colors.hover, color: "#818cf8" },
                        }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={async () => {
                          if (
                            await confirm({
                              title: "Delete bank account?",
                              message: "This bank account will be permanently removed.",
                              danger: true,
                              confirmLabel: "Delete",
                            })
                          )
                            handleDelete(acc.id);
                        }}
                        sx={{
                          color: colors.textMuted,
                          "&:hover": {
                            background: "rgba(239, 68, 68, 0.1)",
                            color: "#ef4444",
                          },
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Box>
            ))
          )}
        </Box>
      </GlassCard>

      {/* Add / Edit modal */}
      <BankAccountModal
        open={modalOpen}
        onClose={closeModal}
        onSuccess={handleModalSuccess}
        editId={editId}
      />

      {/* View drawer (eye icon) */}
      <BankAccountDetailDrawer
        account={viewAccount}
        open={!!viewAccount}
        onClose={() => setViewAccount(null)}
        onEdit={openEdit}
        canEdit={canEdit}
      />
    </DashboardLayout>
  );
}

export default BankAccounts;
